import { Head } from "@inertiajs/react";
import L from "leaflet";
import { MapPin } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker } from "react-leaflet";
import { SalonpageTileLayer } from "./_map-tile-layer";
import { SalonpageFooterInfo } from "./_footer";
import { GoogleMapsFloatingLink } from "./_google_maps_floating_link";
import { NoroshiReserveCta } from "./_noroshi-reserve-cta";
import { NoroshiReserveHost } from "./_noroshi-reserve-host";

interface OverviewSalon {
  id: string;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  categories: string[];
  industry: string | null;
  access?: string | null;
  businessHours?: string | null;
  holiday?: string | null;
}

interface OverviewProps {
  salon: OverviewSalon;
  featureToggles?: {
    noroshiReserve?: boolean | number | null;
  };
  meta: {
    title: string;
    description: string;
  };
}

const pinIcon = L.divIcon({
  className: "hpb-salon-pin",
  html: `
    <div style="transform: translate(-50%, -100%);">
      <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 1.5C6.753 1.5 2.5 5.753 2.5 11c0 7.75 9.5 19.5 9.5 19.5S21.5 18.75 21.5 11C21.5 5.753 17.247 1.5 12 1.5Z"
          fill="#111827"
        />
        <circle cx="12" cy="11" r="4" fill="#F9FAFB"/>
      </svg>
    </div>
  `,
  iconSize: [24, 32],
  iconAnchor: [12, 32],
});

export default function SalonpageOverview({ salon, featureToggles, meta }: OverviewProps) {
  if (!salon) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf7] text-[#0a0a0a] font-serif">
        <div className="text-center">
          <h1 className="text-xl mb-4">サロンが見つかりません</h1>
          <a href="/salons" className="text-sm text-black/50 underline">トップへ戻る</a>
        </div>
      </div>
    );
  }

  const hasLocation = typeof salon.latitude === "number" && typeof salon.longitude === "number";
  const displayAddress = salon.address?.normalize("NFKC").trim() ?? "";

  return (
    <div className="min-h-screen bg-gray-100">
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>
      <div className="mx-auto w-full max-w-md overflow-hidden rounded-b-2xl bg-white shadow-lg">
        <header className="border-b border-gray-100 px-5 py-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Hairbook
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">
            {salon.name}
          </h1>
          {salon.industry && (
            <p className="mt-1 text-sm text-gray-500">{salon.industry}</p>
          )}
        </header>

        <main className="px-5 pt-5">
          <section className="space-y-3 pb-5">
            <NoroshiReserveCta
              salonId={salon.id}
              enabled={featureToggles?.noroshiReserve}
              mode="overview"
              className="inline-flex h-12 w-full items-center justify-center rounded-md bg-[#070a18] px-4 text-sm font-bold text-white shadow-[0_10px_24px_rgba(7,10,24,0.18)]"
            >
              Web予約する
            </NoroshiReserveCta>
          </section>

          <section className="py-5">
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-4">
              <div className="space-y-3.5 text-sm">
                <OverviewInfoRow
                  label="カテゴリ"
                  value={salon.categories.length > 0 ? salon.categories.join(" / ") : "-"}
                  align="right"
                />

                {salon.access && (
                  <OverviewInfoRow label="アクセス" value={salon.access} multiline />
                )}

                {salon.businessHours && (
                  <OverviewInfoRow label="営業時間" value={salon.businessHours} multiline />
                )}

                {salon.holiday && (
                  <OverviewInfoRow label="定休日" value={salon.holiday} multiline />
                )}
              </div>
            </div>
          </section>

          {hasLocation && (
            <section className="relative z-0 -mx-5">
              {displayAddress && (
                <div className="mx-5 mb-3 flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700">
                    <MapPin className="size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-500">所在地</p>
                    <p className="mt-1 text-[13px] font-semibold leading-5 text-gray-900">
                      {displayAddress}
                    </p>
                  </div>
                </div>
              )}
              <div className="overflow-hidden border-y border-gray-100 shadow-sm">
                <div className="relative">
                  <MapContainer
                    center={[salon.latitude as number, salon.longitude as number]}
                    zoom={16}
                    scrollWheelZoom={false}
                    className="h-56 w-full z-0"
                    attributionControl={false}
                  >
                    <SalonpageTileLayer />
                    <Marker
                      position={[salon.latitude as number, salon.longitude as number]}
                      icon={pinIcon}
                    />
                  </MapContainer>
                  <GoogleMapsFloatingLink
                    latitude={salon.latitude as number}
                    longitude={salon.longitude as number}
                  />
                </div>
              </div>
            </section>
          )}
        </main>
      </div>

      <div className="mx-auto w-full max-w-md">
        <NoroshiReserveHost salonId={salon.id} enabled={featureToggles?.noroshiReserve} />
        <SalonpageFooterInfo className="px-4 pb-8 pt-5" />
      </div>
    </div>
  );
}

interface OverviewInfoRowProps {
  label: string;
  value: string;
  align?: "left" | "right";
  multiline?: boolean;
}

function OverviewInfoRow({ label, value, align = "left", multiline = false }: OverviewInfoRowProps) {
  if (multiline) {
    return (
      <div className="flex flex-col gap-1 border-t border-gray-200/60 pt-3 first:border-t-0 first:pt-0">
        <span className="text-gray-500 font-medium">{label}</span>
        <span className="text-gray-900 leading-relaxed font-semibold">{value}</span>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4 border-t border-gray-200/60 pt-3 first:border-t-0 first:pt-0">
      <span className="shrink-0 text-gray-500 font-medium">{label}</span>
      <span className={`text-gray-900 font-semibold leading-relaxed ${align === "right" ? "text-right" : "text-left"}`}>
        {value}
      </span>
    </div>
  );
}
